'use client';

import { useEffect, useRef, useState } from 'react';
import { playSound } from '../sound';
import styles from './motion.module.css';

/**
 * Motion.
 *
 * The brand is flat, rectangular and typographic, and its motion has to be the
 * same thing: type and fields arriving, never bouncing, springing, blurring or
 * parallaxing. Every duration and curve comes from the tokens in
 * `apps/app/src/ui/tokens.css`, so motion is as constrained as colour is.
 *
 * Two things live here. `Reveal` is a block arriving as it is scrolled to, in
 * one of two registers — a rise, or a hard-edged wipe. `SceneWipe` is the
 * transition between pages: a blade of the panel's accent crossing the window
 * once per navigation, which is the same gesture the boot screen's sweep is,
 * at window scale. The other half of that transition is not here at all — it is
 * `.pageMain` in `ui.module.css`, which deals a page's blocks in behind the
 * blade, because it needs no script and a CSS animation is the whole of it.
 *
 * ## Why this is JavaScript at all
 *
 * A scroll-triggered reveal has no CSS-only implementation that works
 * everywhere — `animation-timeline: view()` is Chromium-only today — so it is
 * an IntersectionObserver. One observer per element, disconnected the moment it
 * fires, because a reveal is a one-off and an observer that keeps watching is a
 * scroll listener nobody asked for.
 *
 * ## What happens without JavaScript
 *
 * Everything is visible. The hidden state is applied by the component after it
 * mounts, never in the server-rendered markup, so a page whose JavaScript fails
 * to load shows its content rather than a column of invisible sections. Getting
 * this backwards is the single most common way a reveal animation takes a site
 * down for a subset of users, and it fails invisibly — the page looks empty and
 * nothing errors.
 */

/**
 * Reveals its children when they scroll into view.
 *
 * `as` keeps the element semantic: a revealed section should still be a
 * `<section>`, not a `<div>` wrapping one.
 */
export function Reveal({
  children,
  as: Tag = 'div',
  delay = 0,
  variant = 'rise',
  className,
}: {
  children: React.ReactNode;
  as?: 'div' | 'section' | 'header' | 'li' | 'article';
  /** Stagger, in multiples of `--gb-stagger`. Keep it under about four. */
  delay?: number;
  /**
   * How it arrives.
   *
   * `rise` is the short lift and fade this component has always done, and is
   * the right default for a block of prose: it is barely a movement, and a page
   * of them reads as the page settling.
   *
   * `wipe` uncovers the block from behind a hard horizontal edge, with no fade
   * at all. It is the more cinematic of the two and it is the more *brand* of
   * the two — the system is flat rectangles and its existing motion vocabulary
   * is already three wipes (`gb-bar-grow`, `gb-wipe`, `gb-boot-sweep`), so an
   * edge travelling across a heading belongs here in a way a fade never quite
   * did. Reach for it on the one or two headings a page is built around; using
   * it on every paragraph turns a title card into a tic.
   *
   * **Type and rules only, and nothing focusable.** A wipe is a `clip-path`,
   * `clip-path` cannot be transitioned back to `none`, and so a wiped block is
   * clipped to its own box for good. Two things may therefore not go inside
   * one: anything that draws outside itself — a capsule's accent halo stands
   * twelve pixels proud of the card and would be shaved off for the rest of the
   * visit — and anything that takes focus, because a focus ring is an `outline`
   * two pixels *outside* the element and the clip would cut it off at the
   * section's edge. Wrap arguments and headings; leave shelves and links to
   * `rise`. See `motion.module.css`.
   */
  variant?: 'rise' | 'wipe';
  className?: string | undefined;
}): React.ReactElement {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);
  /**
   * Whether the hidden state is safe to apply yet.
   *
   * False during server render and the first client render, so the markup that
   * ships is the visible one. Only after mount — when JavaScript is provably
   * running and can therefore also reveal it — does anything get hidden.
   */
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Somebody who asked for less motion gets the content, immediately, with no
    // observer at all.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(true);
      return;
    }

    // Already on screen at mount — above the fold — so there is nothing to
    // reveal and hiding it first would produce a flash.
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9) {
      setArmed(true);
      setShown(true);
      return;
    }

    setArmed(true);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          setShown(true);
          // One-off. An observer that keeps watching is a scroll listener
          // nobody asked for.
          observer.disconnect();
        }
      },
      // Fires slightly before the element's edge, so it is finished arriving by
      // the time it is properly in view rather than animating under the reader.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as React.Ref<never>}
      className={`${styles.reveal} ${className ?? ''}`.trim()}
      data-armed={armed ? 'true' : undefined}
      data-shown={shown ? 'true' : undefined}
      data-variant={variant === 'wipe' ? 'wipe' : undefined}
      style={delay > 0 ? { transitionDelay: `calc(var(--gb-stagger) * ${delay})` } : undefined}
    >
      {children}
    </Tag>
  );
}

/**
 * Reveals its children one after another.
 *
 * A grid of capsules arriving together is a page loading; arriving in sequence
 * is a page being dealt out, which is the difference the store wanted. The
 * stagger is capped so a long grid does not turn into a queue — past about the
 * sixth item everything arrives at once, because nobody is watching item
 * nineteen wait its turn.
 */
/**
 * The scene change.
 *
 * A blade of the panel's accent crosses the window, once, every time the reader
 * arrives somewhere new. Behind it the page deals itself in — see `.pageMain`
 * in `ui.module.css`, which is the other half of this and does the actual
 * arriving. Together they are the difference between a document that swapped
 * its contents and a place you moved to.
 *
 * **It is the boot screen's sweep, reused.** `gb-boot-sweep` is a flat block of
 * the accent travelling at a constant speed across a fixed track, and that is
 * already the product's word for *something is happening*. Doing scene changes
 * in a second, unrelated device would have meant two vocabularies for one idea;
 * this is the same one at window scale.
 *
 * ## Why it cannot get in the way
 *
 * `pointer-events: none` and `aria-hidden`, so it is scenery in the strict
 * sense: a click during the sweep lands on whatever is under it, and nothing
 * announces it. It also never covers the window — the blade is a fraction of
 * the width and always moving, so at no point is the page hidden behind it and
 * at no point is there a frame to wait for. A transition that blanks the screen
 * to cover a render is a transition that has to be *fast*; this one is free to
 * be slow because nothing is behind it.
 *
 * ## How it replays
 *
 * The blade is keyed on `scene`, so React replaces the element rather than
 * updating it and the CSS animation starts again from the top. That is the
 * whole mechanism — no state, no effect, no timer to clear, and nothing that
 * can be left running by a navigation that happened mid-sweep.
 *
 * Deliberately no exit half. A wipe that covers and then uncovers is two
 * gestures and roughly twice as long, and the second one is the half that has
 * to hide the page to work.
 */
export function SceneWipe({
  scene,
}: {
  /** Changes when the reader arrives somewhere new. The path, in practice. */
  scene: string;
}): React.ReactElement {
  const sceneAlreadyShown = useRef<string | null>(null);

  useEffect(() => {
    const isTheFirstSceneOfTheSession = sceneAlreadyShown.current === null;
    sceneAlreadyShown.current = scene;
    if (!isTheFirstSceneOfTheSession) playSound('sceneChanged');
  }, [scene]);

  return (
    <div className={styles.scene} aria-hidden="true">
      <i key={scene} className={styles.sceneBlade} />
    </div>
  );
}

export function RevealGroup({
  children,
  as: Tag = 'div',
  className,
  maxStagger = 6,
}: {
  children: React.ReactNode;
  as?: 'div' | 'section' | 'ul';
  className?: string | undefined;
  maxStagger?: number;
}): React.ReactElement {
  const items = Array.isArray(children) ? children : [children];

  return (
    <Tag className={className}>
      {items.map((child, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Reveal key={index} delay={Math.min(index, maxStagger)}>
          {child}
        </Reveal>
      ))}
    </Tag>
  );
}
